import {
  Alert,
  AlertDescription,
} from "@reactive-resume/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";

import { setCoachMetadata } from "@/client/libs/coach-metadata";
import { useCoachIntegration } from "@/client/services/auth";

const decodeTokenPayload = (token: string) => {
  const payload = token.split(".")[1];
  if (!payload) throw new Error("Invalid token payload.");

  const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
  const paddedPayload = normalizedPayload.padEnd(
    normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
    "=",
  );

  return JSON.parse(atob(paddedPayload));
};

export const CoachIntegration = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const { coachIntegration, loading } = useCoachIntegration();
  const token = searchParams.get("token");
  const hasCalledRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate calls (React StrictMode / re-renders)
    if (hasCalledRef.current) return;

    // Only proceed if token exists
    if (!token) {
      setError("Missing authentication token. Please try again.");
      return;
    }

        // Where to land once authenticated. A token minted by the LMS's
    // coach-resume/prepare carries `coachJob`, which means the student came here to
    // tailor a resume for a specific job rather than to browse their resumes.
    //
    // This decode is for ROUTING ONLY. It is unverified client-side, so nothing
    // security-relevant may depend on it — the server verifies the same token's
    // signature in POST /auth/coachIntegration before issuing any session.
    let redirectTo: string | undefined;

    try {
      const decodedPayload = decodeTokenPayload(token);

      const decodedData =
        typeof decodedPayload.data === "string" ? JSON.parse(decodedPayload.data) : decodedPayload.data;

      const organizationName = decodedData?.obj?.organizationName ?? "";
      const programName = decodedData?.obj?.programName ?? "";

      setCoachMetadata(organizationName, programName);

      const jobUuid = decodedData?.obj?.coachJob?.jobUuid;
      if (jobUuid) {
        redirectTo = `/coach/optimize?job=${encodeURIComponent(jobUuid)}`;
      }
    } catch (err) {
      console.error("Failed to decode coach token:", err);
    }

    // Call the integration function
    coachIntegration(token, redirectTo).catch((err) => {
      console.error("Coach integration error:", err);
      setError(err?.response?.data?.message || err?.message || "Failed to authenticate. Please try again.");
      hasCalledRef.current = false; // Allow retry on error
    });
  }, [token]);

  if (error) {
    return (
      <div className="space-y-8">
        <Helmet>
          <title>{`Coach LMS Integration - Error`}</title>
        </Helmet>

        <div className="space-y-4">
          {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
          <h2 className="text-2xl font-semibold tracking-tight">Authentication Error</h2>
          <Alert variant="error">
            <AlertDescription className="pt-0">
              {error}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Helmet>
        <title>{`Coach LMS Integration`}</title>
      </Helmet>
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="space-y-4 text-center">
          <div className="text-lg font-medium">
            {loading ? "Authenticating..." : "Processing authentication..."}
          </div>
          {loading && (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
